import React, { useEffect, useState } from 'react';
import { Mail, Phone, Check, RotateCcw, Inbox } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { CONTACT_SUBJECTS } from '@/pages/Contact';

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  subject: string;
  message: string;
  language: string;
  status: string;
  created_at: string;
}

/** Admin-only inbox for messages submitted from the public Contact / Support page. */
const AdminInbox: React.FC = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState<ContactMessage[]>([]);
  const [filter, setFilter] = useState<'all' | 'new'>('new');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('mkt_contact_messages')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data }) => {
        setRows((data || []) as ContactMessage[]);
        setLoading(false);
      });
  }, []);

  const setStatus = async (row: ContactMessage, status: 'new' | 'resolved') => {
    await supabase
      .from('mkt_contact_messages')
      .update({ status, resolved_at: status === 'resolved' ? new Date().toISOString() : null })
      .eq('id', row.id);
    setRows((p) => p.map((x) => (x.id === row.id ? { ...x, status } : x)));
  };

  const subjectLabel = (value: string) => {
    const s = CONTACT_SUBJECTS.find((x) => x.value === value);
    return s ? t(s.key) : value;
  };

  const visible = filter === 'all' ? rows : rows.filter((r) => r.status !== 'resolved');
  const unresolved = rows.filter((r) => r.status !== 'resolved').length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
          <Inbox className="h-5 w-5 text-orange-500" />
          {t('admin.inbox')}
          {unresolved > 0 && (
            <span className="rounded-full bg-orange-500 px-2.5 py-0.5 text-xs font-bold text-white">{unresolved}</span>
          )}
        </h2>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {(['new', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 text-xs font-bold ${
                filter === f ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {f === 'new' ? t('admin.filterNew') : t('admin.filterAll')}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {loading ? (
          <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            {t('common.loading')}
          </p>
        ) : visible.length === 0 ? (
          <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            {t('admin.inboxEmpty')}
          </p>
        ) : (
          visible.map((m) => (
            <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-bold text-slate-900">{m.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-orange-600">
                      <Mail className="h-3.5 w-3.5" /> {m.email}
                    </a>
                    {m.phone && (
                      <a href={`tel:${m.phone}`} className="flex items-center gap-1 hover:text-orange-600">
                        <Phone className="h-3.5 w-3.5" /> {m.phone}
                      </a>
                    )}
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                    <span className="uppercase">{m.language}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                    {subjectLabel(m.subject)}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                      m.status === 'resolved' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'
                    }`}
                  >
                    {m.status === 'resolved' ? t('admin.resolved') : t('admin.new')}
                  </span>
                </div>
              </div>

              <p className="mt-3 whitespace-pre-line rounded-xl bg-slate-50 p-4 text-sm text-slate-700">{m.message}</p>

              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${subjectLabel(m.subject)}`)}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                >
                  <Mail className="h-3.5 w-3.5" /> {t('admin.replyEmail')}
                </a>
                {m.status === 'resolved' ? (
                  <button
                    onClick={() => setStatus(m, 'new')}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> {t('admin.reopen')}
                  </button>
                ) : (
                  <button
                    onClick={() => setStatus(m, 'resolved')}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    <Check className="h-3.5 w-3.5" /> {t('admin.markResolved')}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminInbox;
